import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  Archive,
  Bell,
  BookOpen,
  CalendarDays,
  ClipboardList,
  Clock3,
  HandCoins,
  Home,
  LineChart,
  LogOut,
  MapPin,
  Menu,
  MessageCircle,
  Search,
  Settings,
  Shield,
  ShieldAlert,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { GlobalStudentSearch } from "@/components/layout/GlobalStudentSearch";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useAuth } from "@/contexts/AuthContext";
import { useEscolaConfig } from "@/contexts/EscolaConfigContext";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface SidebarProps {
  children: ReactNode;
}

interface NavigationItem {
  title: string;
  icon: LucideIcon;
  href: string;
  roles?: string[];
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

const Sidebar = ({ children }: SidebarProps) => {
  const { user, logout } = useAuth();
  const { config } = useEscolaConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasArquivos, setHasArquivos] = useState(false);

  useEffect(() => {
    const checkArquivos = async () => {
      if (!user?.escola_id) return;

      try {
        const { count } = await supabase
          .from("anos_letivos")
          .select("id", { count: "exact", head: true })
          .eq("escola_id", user.escola_id)
          .eq("status", "arquivado");

        setHasArquivos((count ?? 0) > 0);
      } catch (error) {
        console.error("Erro ao verificar arquivos:", error);
      }
    };

    void checkArquivos();
  }, [user?.escola_id]);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const navGroups: NavigationGroup[] = [
    {
      label: "Visão geral",
      items: [
        { title: "Página inicial", icon: Home, href: "/dashboard" },
        {
          title: "Painel do gestor",
          icon: LineChart,
          href: "/gestor/dashboard",
          roles: ["admin", "diretor"],
        },
      ],
    },
    {
      label: "Acadêmico",
      items: [
        { title: "Turmas e chamadas", icon: UserCheck, href: "/dashboard" },
        { title: "Faltas e atrasos", icon: Search, href: "/consultar-faltas" },
        { title: "Registrar atrasos", icon: Clock3, href: "/registro-atrasos" },
        { title: "Atestados", icon: ClipboardList, href: "/atestados" },
        { title: "Disciplinas", icon: BookOpen, href: "/disciplinas" },
        ...(FEATURE_FLAGS.MAPA_ALUNOS
          ? [{
              title: "Mapa de alunos",
              icon: MapPin,
              href: "/mapa",
              roles: ["admin", "diretor", "coordenador"],
            }]
          : []),
      ],
    },
    {
      label: "Relacionamento",
      items: [
        { title: "Busca ativa", icon: ShieldAlert, href: "/busca-ativa" },
        ...(FEATURE_FLAGS.WHATSAPP_BOT
          ? [{
              title: "WhatsApp",
              icon: MessageCircle,
              href: "/gestor/whatsapp-bot",
              roles: ["admin", "diretor", "coordenador", "secretario"],
            }]
          : []),
        {
          title: "Notificações",
          icon: Bell,
          href: "/notificacoes",
          roles: ["admin", "diretor"],
        },
      ],
    },
    {
      label: "Gestão",
      items: [
        {
          title: "Programas sociais",
          icon: HandCoins,
          href: "/gestor/programas",
          roles: ["admin", "diretor"],
        },
        {
          title: "Ano letivo",
          icon: CalendarDays,
          href: "/ano-letivo",
          roles: ["admin", "diretor", "secretario"],
        },
        ...(hasArquivos
          ? [{
              title: "Arquivos",
              icon: Archive,
              href: "/arquivos",
              roles: ["admin", "diretor", "secretario"],
            }]
          : []),
        {
          title: "Gestão de acessos",
          icon: Shield,
          href: "/gestao-acesso",
          roles: ["admin"],
        },
        {
          title: "Perfil da escola",
          icon: Settings,
          href: "/perfil-escola",
          roles: ["admin"],
        },
      ],
    },
  ];

  const isActive = (href: string) => {
    if (href === "/dashboard") return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  const getActiveStyle = (active: boolean) => {
    if (!active) return undefined;
    const primaryColor = config?.cor_primaria || "#4f46e5";

    return {
      backgroundColor: `${primaryColor}14`,
      borderColor: `${primaryColor}35`,
      color: primaryColor,
    };
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col bg-white text-slate-700">
      <div
        className="flex items-center gap-3 border-b border-slate-200 p-4"
        style={{ backgroundColor: config?.cor_secundaria || "#f8fafc" }}
      >
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl shadow-sm"
          style={{ backgroundColor: config?.cor_primaria || "#4f46e5" }}
        >
          {config?.url_logo ? (
            <img src={config.url_logo} alt="" className="h-7 w-7 object-contain" />
          ) : (
            <Home className="h-5 w-5 text-white" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold leading-tight text-slate-950">
            {config?.nome || "Chamada Diária"}
          </p>
          <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            Gestão escolar
          </p>
        </div>
      </div>

      <div className="border-b border-slate-100 p-3">
        <GlobalStudentSearch />
      </div>

      <nav
        className="flex-1 space-y-5 overflow-y-auto p-3 scrollbar-thin"
        aria-label="Navegação principal"
      >
        {navGroups.map(group => {
          const visibleItems = group.items.filter(
            item => !item.roles || item.roles.includes(user?.role || ""),
          );
          if (!visibleItems.length) return null;

          return (
            <div key={group.label}>
              <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                {group.label}
              </p>
              <div className="space-y-1">
                {visibleItems.map(item => {
                  const active = isActive(item.href);

                  return (
                    <Link
                      key={`${group.label}-${item.title}`}
                      to={item.href}
                      className={cn(
                        "flex min-h-10 items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-950",
                        active && "font-semibold",
                      )}
                      style={getActiveStyle(active)}
                      onClick={() => setIsOpen(false)}
                      aria-current={active ? "page" : undefined}
                    >
                      <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 min-w-0 px-3">
          <p className="truncate text-xs font-medium text-slate-700">
            {user?.username || user?.email || "Usuário"}
          </p>
          <p className="mt-0.5 text-[10px] capitalize text-slate-500">{user?.role || "equipe"}</p>
        </div>
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="h-10 w-full justify-start gap-3 text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sair
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-[100dvh] min-h-0 w-full overflow-hidden bg-slate-50">
      <aside className="z-20 hidden w-72 flex-col border-r border-slate-200 bg-white shadow-sm md:flex">
        <SidebarContent />
      </aside>

      <div className="relative flex h-full min-h-0 min-w-0 w-full flex-1 flex-col">
        <div className="sticky top-0 z-20 flex items-center border-b border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur md:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="app-touch-target -ml-1 mr-2 text-slate-700"
                aria-label="Abrir menu principal"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[88vw] max-w-xs bg-white p-0">
              <SidebarContent />
            </SheetContent>
          </Sheet>

          <div className="mr-10 flex min-w-0 flex-1 items-center justify-center">
            <p className="truncate text-center text-base font-bold text-slate-900">
              {config?.nome || "Chamada Diária"}
            </p>
          </div>
        </div>

        <main className="min-h-0 flex-1 touch-pan-y overflow-x-hidden overflow-y-auto overscroll-y-auto p-4 pb-24 [-webkit-overflow-scrolling:touch] md:p-6 md:pb-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Sidebar;
